/* eslint-disable @typescript-eslint/no-unused-vars */
// 2) Third-party library imports
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';

// 3c) Project-specific modules - components / types
import {
    NotificationsState,
    GetCorpayCurrenciesForCompanyRequestPayload,
    Notification,
} from './types';
import type { RootState } from '../storeTypes';

// 3d) Utility functions / interfaces
import { DynamicObject, ResponseModalObject } from '../../utils/commonInterfaces';

const actionPrefix = 'notification';

// Type-safe initialState!
export const initialState: NotificationsState = {
    categorizedNotifications: {
        loading: false,
        errorMessages: [],
        data: [],
    },
    notificationsByCategory: {
        loading: false,
        errorMessages: [],
        data: [],
    },
    unreadNotificationCategories: [],
    announcementsFetching: false,
    refetchNewNotifications: false,
};

const notificationSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getCategorizedNotificationsRequestAction: {
            reducer: (
                state,
                action: PayloadAction<{
                    companyId: string | null,
                    callback?: (notifications: unknown) => void;
                    noState?: boolean
                }>) => {
                state.categorizedNotifications.loading = !action.payload.noState;
            },
            prepare: (
                companyId: string | null,
                callback?: (notifications: unknown) => void,
                noState?: boolean
            ) => ({
                payload: {
                    companyId,
                    callback,
                    noState
                }
            })
        },
        getCategorizedNotificationsSuccessAction: (
            state,
            action: PayloadAction<{
                data: Notification[];
            }>
        ) => {
            state.categorizedNotifications.data = action.payload.data;
            state.categorizedNotifications.loading = false;
            state.categorizedNotifications.errorMessages = initialState.categorizedNotifications.errorMessages;
        },
        getCategorizedNotificationsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.categorizedNotifications.data = initialState.categorizedNotifications.data;
            state.categorizedNotifications.loading = false;
            state.categorizedNotifications.errorMessages = action.payload;
        },
        getNotificationsByCategoryRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    companyId: string | null;
                    categoryId: string;
                }>
            ) => {
                state.notificationsByCategory.loading = true;
            },
            prepare: (
                companyId: string | null,
                categoryId: string
            ) => ({
                payload: {
                    companyId,
                    categoryId
                }
            })
        },
        getNotificationsByCategorySuccessAction: (
            state,
            action: PayloadAction<{ data: Notification[] }>
        ) => {
            state.notificationsByCategory.data = action.payload.data;
            state.notificationsByCategory.loading = false;
            state.notificationsByCategory.errorMessages = initialState.notificationsByCategory.errorMessages;
        },
        getNotificationsByCategoryErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.notificationsByCategory.data = initialState.notificationsByCategory.data;
            state.notificationsByCategory.loading = false;
            state.notificationsByCategory.errorMessages = action.payload;
        },
        clearCategorizedNotificationsStateDataAction: (state) => {
            state.categorizedNotifications = initialState.categorizedNotifications;
        },
        clearNotificationsByCategoryStateDataAction: (state) => {
            state.notificationsByCategory = initialState.notificationsByCategory;
        },
        getUnreadNotificationsForUserSuccessAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.unreadNotificationCategories = action.payload;
        },
        setLoginProductAnnouncementFetching: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.announcementsFetching = action.payload;
        },
        triggerRefetchNotificationsBooleanAction: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.refetchNewNotifications = action.payload;
        },
    }    
});

const { actions, reducer } = notificationSlice;

export const {
    getCategorizedNotificationsRequestAction,
    getCategorizedNotificationsSuccessAction,
    getCategorizedNotificationsErrorAction,
    getNotificationsByCategoryRequestAction,
    getNotificationsByCategorySuccessAction,
    getNotificationsByCategoryErrorAction,
    clearCategorizedNotificationsStateDataAction,
    clearNotificationsByCategoryStateDataAction,
    getUnreadNotificationsForUserSuccessAction,
    setLoginProductAnnouncementFetching,
    triggerRefetchNotificationsBooleanAction,
} = actions;

export const fetchNewNotificationsAction = createAction(
    `${actionPrefix}/fetchNewNotificationsAction`,
    function prepare(
        latestNotificationDateTime: string,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                latestNotificationDateTime,
                callback
            }
        };
    }
);

export const fetchNotificationDetailsPublicAction = createAction(
    `${actionPrefix}/fetchNotificationDetailsPublicAction`,
    function prepare(
        notificationId: string,
        applyDelay: boolean,
        isPaymentPlanEnabled: boolean,
        invoicesAndCreditsOnly: boolean,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                notificationId,
                applyDelay,
                isPaymentPlanEnabled,
                invoicesAndCreditsOnly,
                callback
            }
        };
    }
);

export const getUnreadNotificationsForUserRequestAction = createAction(
    `${actionPrefix}/getUnreadNotificationsForUserRequestAction`,
    function prepare(
        companyId: string
    ) {
        return {
            payload: {
                companyId
            }
        };
    }
);

export const getNotificationLetterPresignedUrlAction = createAction(
    `${actionPrefix}/getNotificationLetterPresignedUrlAction`,
    function prepare(
        fileId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                fileId,
                callback
            }
        };
    }
);

export const getTicketOptionsRequestAction = createAction(
    `${actionPrefix}/getTicketOptionsRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject[]) => void
    ) {
        return {
            payload: {
                companyId,
                callback
            }
        };
    }
);

export const raiseQueryRequestAction = createAction(
    `${actionPrefix}/raiseQueryRequestAction`,
    function prepare(
        payload: {}
    ) {
        return {
            payload
        };
    }
);

export const markNotificationAsReadAction = createAction(
    `${actionPrefix}/markNotificationAsReadAction`,
    function prepare(
        categoryId: string,
        notificationId?: string,
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: {
                categoryId,
                notificationId,
                callback
            }
        };
    }
);

export const payUsingCreditsRequestAction = createAction(
    `${actionPrefix}/payUsingCreditsRequestAction`,
    function prepare(
        payload: {},
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }
);

export const getPaymentDataPublicRequestAction = createAction(
    `${actionPrefix}/getPaymentDataPublicRequestAction`,
    function prepare(
        paymentId: string,
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                paymentId,
                companyId,
                callback
            }
        };
    }
);

export const getProductAnnouncementsRequestAction = createAction(
    `${actionPrefix}/getProductAnnouncementsRequestAction`,
    function prepare(
        minDate: string,
        maxDate: string,
        callback: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                minDate,
                maxDate,
                callback
            }
        };
    }
);

export const getCorpayCurrenciesForCompanyRequestAction = createAction(
    `${actionPrefix}/getCorpayCurrenciesForCompanyRequestAction`,
    function prepare(
        payload: GetCorpayCurrenciesForCompanyRequestPayload
    ) {
        return {
            payload
        };
    }
)

export const getPayingCountriesForTransferMateRequestAction = createAction(
    `${actionPrefix}/getPayingCountriesForTransferMateRequestAction`,
    function prepare(
        payload: GetCorpayCurrenciesForCompanyRequestPayload
    ) {
        return {
            payload
        };
    }
)

export const confirmDetailsForFXConversionRequestAction = createAction(
    `${actionPrefix}/confirmDetailsForFXConversionRequestAction`,
    function prepare(
        payload: {},
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }
)

export const makeTransfermatePaymentRequestAction = createAction(
    `${actionPrefix}/makeTransfermatePaymentRequestAction`,
    function prepare(
        payload: {},
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }
)

// Selectors
export const getNotificationsState = (state: RootState) => state.notifications;
export const getAnnouncementsFetching = createSelector(
    getNotificationsState,
    (notifications) => notifications.announcementsFetching
);
export const getCategorizedNotifications = createSelector(
    getNotificationsState,
    (notifications) => notifications.categorizedNotifications
);
export const getCategorizedNotificationsData = createSelector(
    getCategorizedNotifications,
    (categorizedNotifications) => categorizedNotifications.data
);
export const getNotificationsByCategory = createSelector(
    getNotificationsState,
    (notifications) => notifications.notificationsByCategory
);
export const getUnreadNotificationCategories = createSelector(
    getNotificationsState,
    (notifications) => notifications.unreadNotificationCategories
);
export const getUnreadNotifications = createSelector(
    getNotificationsState,
    (notifications) => notifications.unreadNotificationCategories
);
export const getUnreadNotificationsCount = createSelector(
    getUnreadNotifications,
    (unreadNotifications) => unreadNotifications.length
);

// Reducer
export default reducer;